import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Popover } from "antd";
import Button from "Components/UI/Button";
import { Icon } from "Components/UI";
import Select, { IOptionData } from "Components/UI/Select";
import Input from "Components/UI/Input";
import { GetAvatar } from "Utils/generateThumbnail";
import {
  arrayTopicKeysPtBr,
  operationsFormatObj,
  optionsOperationsObj,
} from "./helper";
import useFilterDropdown from "./useFilterDropdown";
import FilterDatePicker from "./components/FilterDatePicker";
import { dateToLocaleString } from "Utils/DateUtils";
import {
  arrayNotificationKeys,
  IFiltroOptions,
  IUpdateFilter,
  IUserDescription,
} from "Data/interfaces/Notification/IFilters";
import SearchInput from "Components/UI/SearchInput";
import {
  Container,
  Content,
  Title,
  Check,
  MainContent,
  ShowComplex,
  SearchContent,
} from "./styles";

interface IFilterDropdown {
  id: string;
  item?: IFiltroOptions;
  firstOpenFilters: boolean;
  blockFilterDropdown: boolean;
  updateFilter: (values: IUpdateFilter) => void;
  deleteFilter: (filterId: string) => void;
};

const FilterDropdown: FC<IFilterDropdown> = ({
  id,
  item,
  firstOpenFilters,
  blockFilterDropdown,
  updateFilter,
  deleteFilter,
}) => {
  const { t } = useTranslation('notification');
  const [open, setOpen] = useState(
    (firstOpenFilters || blockFilterDropdown) ? false : true
  );
  const [search, setSearch] = useState('');

  const {
    checkOptions,
    hasCheck,
    handleKeyUpdateFilter,
    handleOperationUpdateFilter,
    handleValueUpdateFilter,
    theme,
  } = useFilterDropdown({
    id,
    item,
    search,
    updateFilter,
  });

  const optionsKeys = useMemo(() => {
    const options: IOptionData[] = arrayNotificationKeys.map(key => ({
      value: key,
      description: t(`filters.topicKey.${key}`, { defaultValue: arrayTopicKeysPtBr[key] }),
      label: t(`filters.topicKey.${key}`, { defaultValue: arrayTopicKeysPtBr[key] }),
    }));

    return options;
  }, [t]);

  const optionsOperations = useMemo(() => {
    let options: IOptionData[] = [];

    if (item?.key) {
      options = optionsOperationsObj[item.key].map(op => operationsFormatObj(t)[op]());
    }

    return options;
  }, [item?.key, t]);

  const title = (
    <Title>
      <span className="textTitle">{t('filters.editFilter')}</span>
      <Button
        type="text"
        className="deleteButton"
        onClick={() => deleteFilter(id)}
      >
        <Icon
          icon="emptyExcluir"
          customSize={14}
          className="deleteIcon"
        />
      </Button>
    </Title>
  );

  const mainContent = () => {
    if (item?.key) {
      if (
        [
          'authorIds',
          'actions',
          'csIds',
          'isRead',
        ].includes(item.key) &&
        hasCheck
      ) {
        return checkOptions.options.map(option => (
          <Checkbox
            key={option.value}
            className="checkBox"
            name={option.value}
            onChange={(e) => handleValueUpdateFilter(
              e.target.value,
              item.key === 'isRead' ? true : false
            )}
            value={option.value}
            checked={
              Array.isArray(item.values)
                ? item.values.includes(option.value)
                : item.values === option.value
            }
          >
            <Check>
              {option.label}
            </Check>
          </Checkbox>
        ));
      }

      if ([''].includes(item.key)) {
        return (
          <Input
            label=""
            placeholder={t('filters.examplePlaceholder')}
            value={item.values}
            onInput={handleValueUpdateFilter}
            inputClassName="inputDefault"
          />
        );
      }

      if (item.key === 'creationDate') {
        return (
          <FilterDatePicker
            operation={item.operation}
            item={item}
            handleValueUpdateFilter={handleValueUpdateFilter}
          />
        );
      }
    }

    return <></>;
  };

  const content = (
    <Content>
      <div className="headerContent">
        <div className="selectContainer">
          <Select
            options={optionsKeys}
            value={item?.key}
            size="small"
            className="select"
            customBackgroundColor={theme.colors.surface.surfaceOutline}
            onChange={handleKeyUpdateFilter}
          />
        </div>
        <div className="selectContainer">
          <Select
            options={optionsOperations}
            value={item?.operation}
            size="small"
            className="select"
            disabled={optionsOperations.length === 1}
            notShowIcon={optionsOperations.length === 1}
            customBackgroundColor={theme.colors.surface.surfaceOutline}
            onChange={handleOperationUpdateFilter}
          />
        </div>
      </div>
      {['authorIds', 'actions', 'csIds'].includes(item?.key || '') && (
        <SearchContent>
          <span className="searchWrapper">
            <SearchInput
              inputPlaceholder={t('filters.searchPlaceholder')}
              inputClassName="searchInput"
              noDebounceValue={search}
              withDebounce={false}
              setSearch={setSearch}
            />
          </span>
        </SearchContent>
      )}
      <MainContent
        hasCheck={hasCheck}
        isDatePicker={item?.key === 'creationDate'}
      >
        {mainContent()}
      </MainContent>
    </Content>
  );

  const descriptionContent = () => {
    if (item?.key && item?.description) {
      if (['authorIds', 'csIds'].includes(item.key)) {
        let showUsers = item.description as IUserDescription[];
        const total = showUsers.length;
        let showCount = '';
        if (total > 5) {
          showCount = (total - 5).toString();
          showUsers = showUsers.slice(0, 6);
        }

        return (
          <ShowComplex customMargin="4px">
            {showUsers.map((user, i) => {
              if (i === 5) {
                return <span className="textOperation">+{showCount}</span>
              }

              return (
                <GetAvatar
                  name={user.name}
                  shape='circle'
                  size={24}
                  src={user.img}
                  border={`1px solid ${theme.colors.surface.surface}`}
                  zIndex={(showUsers.length + 1) - i}
                  marginRight={i === 4 ? '0px' : '-5px'}
                  cursor='pointer'
                />
              )
            })}
          </ShowComplex>
        );
      }

      if (
        ['actions', 'isRead'].includes(item.key) &&
        Array.isArray(item.description)
      ) {
        let showItems = item.description as string[];
        const total = item.description.length;
        let showCount = '';
        if (total > 2) {
          showCount = (total - 2).toString();
          showItems = showItems.slice(0, 3);
        }

        return showItems.map((val, i) => {
          if (i === 2) {
            return <span className="textOperation">+{showCount}</span>
          }

          return (
            <ShowComplex>
              <span className="text">{val}</span>
            </ShowComplex>
          )
        });
      }

      if (item.key === 'creationDate') {
        if (Array.isArray(item.description)) {
          return (
            <span className="textDescription">
              {`${item.description[0]
                ? dateToLocaleString(item.description[0] as string)
                : ''
                }  ➔  ${item.description[1]
                  ? dateToLocaleString(item.description[1] as string)
                  : ''
                }`}
            </span>
          );
        }

        return (
          <span className="textDescription">
            {dateToLocaleString(item.description)}
          </span>
        );
      }
    }

    return <></>;
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  useEffect(() => {
    setSearch('');
  }, [open]);

  return (
    <Container>
      <Popover
        content={content}
        title={title}
        trigger="click"
        placement="bottomLeft"
        open={open}
        onOpenChange={handleOpenChange}
        overlayStyle={{
          width: 350,
          borderRadius: 8,
          border: `1px solid ${theme.colors.surface.surfaceOutline}`,
        }}
      >
        <Button
          type="text"
          className="filterButton"
        >
          <span className="textId">
            {item?.key ? t(`filters.topicKey.${item.key}`, { defaultValue: arrayTopicKeysPtBr[item.key] }) : ''}
          </span>
          {item?.operation && (
            <span className="textOperation">
              {operationsFormatObj(t)[item.operation]().label}
            </span>
          )}
          {descriptionContent()}
          <Icon
            icon="expandir"
            color="cinzaEscuro"
            customSize={8}
            className="buttonIcon"
          />
        </Button>
      </Popover>
    </Container>
  );
};

export default FilterDropdown
